import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';
import Select from '@mds/shared/ui/select';

import AttendeesPanel from '@/components/events/attendees-panel';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import { selectCls } from '@/components/ui/field';
import { eventDateLabel, eventPriceLabel, eventStatusTone, eventWhen } from '@/domain/events';
import * as events from '@/events/events.api';

/** Today as the API writes dates, in the visitor's own calendar. */
const today = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * Who is coming to an event, and who has arrived: pick the event, then work its list. The event
 * is kept in the address (`?event=`), so a link from an event's dialog opens straight on it. With
 * none chosen, the next event on the programme is shown, or the latest one once they are all past.
 */
export default function AttendeesPage() {
  const [params, setParams] = useSearchParams();

  const programme = useQuery({
    queryKey: ['events', 'attendees-picker'],
    queryFn: () => events.listEvents({ limit: 100, sort: 'eventDate', direction: 'desc' }),
  });
  const all = programme.data?.data ?? [];
  const now = today();
  const fallback = [...all].reverse().find((e) => e.eventDate >= now) ?? all[0];
  const eventId = params.get('event') ?? fallback?.id ?? '';

  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => events.getEvent(eventId),
    enabled: eventId !== '',
  });

  if (programme.isPending) return <p className="m-0 text-base text-ink-muted">Loading events…</p>;
  if (programme.isError) {
    return (
      <EmptyState
        title="Could not load the events"
        hint={
          programme.error instanceof ApiError
            ? programme.error.message
            : 'The server could not be reached.'
        }
      />
    );
  }
  if (all.length === 0) {
    return (
      <EmptyState
        title="No events yet"
        hint="Create one from Events, and its attendees appear here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Select
        variant="field"
        className={`${selectCls} max-w-xl`}
        value={eventId}
        onChange={(id) => setParams({ event: id })}
        options={all.map((e) => ({
          value: e.id,
          label: `${e.title} · ${eventDateLabel(e.eventDate)}`,
        }))}
        placeholder="Pick an event"
        aria-label="Event"
      />

      {event.isPending ? (
        <p className="m-0 text-base text-ink-muted">Loading the event…</p>
      ) : event.isError ? (
        <EmptyState
          title="Could not load this event"
          hint={
            event.error instanceof ApiError
              ? event.error.message
              : 'The server could not be reached.'
          }
        />
      ) : (
        <>
          <div className="flex flex-col gap-3">
            <h2 className="m-0 text-3xl leading-tight font-semibold tracking-tighter">
              {event.data.title}
            </h2>
            <div className="flex flex-wrap gap-2">
              <Chip className={'border ' + eventStatusTone(event.data.status).cls}>
                {eventStatusTone(event.data.status).label}
              </Chip>
              <Chip>
                {eventDateLabel(event.data.eventDate)} · {eventWhen(event.data)}
              </Chip>
              <Chip>{event.data.space}</Chip>
              <Chip>{eventPriceLabel(event.data.price.amountMinor)}</Chip>
            </div>
          </div>

          <AttendeesPanel key={event.data.id} event={event.data} />
        </>
      )}
    </div>
  );
}
